import React, { useState, useEffect, useMemo, useReducer, useRef } from "react";


import { isSameDay, parseISO, format, addHours } from "date-fns";

import { green, red } from "@mui/material/colors";
import { Avatar, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Fab, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";

import { makeStyles } from 'tss-react/mui';

import {
  AccessTime,
  ArrowDownward,
  Block,
  Close,
  Done,
  DoneAll,
  ErrorOutlined,
  ExpandMore,
  GetApp,
  InsertDriveFileOutlined,
  Facebook,
  Instagram,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search,
  Star,
  Campaign,
  WhatsApp,
  Language,
  Science,
} from "@mui/icons-material";

import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import AudioPlayer from "../AudioPlayer";
import MessageOptionsMenu from "../MessageOptionsMenu";
import ToolTraceView from "../ToolTraceView";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";


const useStyles = makeStyles()((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  adReferralBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    fontSize: 13,
    lineHeight: 1.35,
    color: theme.palette.mode === "light" ? "#0b6b57" : "#8fe3cf",
    backgroundColor: theme.palette.mode === "light" ? "#e7f7f1" : "#14332b",
    borderBottom: `1px solid ${theme.palette.mode === "light" ? "#c7ece0" : "#1f4a3e"}`,
    "& b": { fontWeight: 700 },
  },

  messagesList: {
    backgroundImage:
      theme.palette.mode === "light"
        ? `url(${whatsBackground})`
        : `linear-gradient(rgba(11,20,26,0.82), rgba(11,20,26,0.82)), url(${whatsBackground})`,
    backgroundColor: theme.palette.mode === "light" ? undefined : "#0b141a",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    userSelect: "text",
    WebkitUserSelect: "text",
    "& *": {
      userSelect: "text",
      WebkitUserSelect: "text",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#202c33",
    color: theme.palette.mode === "light" ? "#303030" : "#e9edef",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.mode === "light" ? "0 1px 1px #b3b3b3" : "0 1px 1px rgba(0,0,0,0.4)",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.mode === "light" ? "#f0f0f0" : "rgba(255,255,255,0.08)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.mode === "light" ? "#dcf8c6" : "#005c4b",
    color: theme.palette.mode === "light" ? "#303030" : "#e9edef",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.mode === "light" ? "0 1px 1px #b3b3b3" : "0 1px 1px rgba(0,0,0,0.4)",
  },

  stickerBubble: {
    backgroundColor: "transparent",
    boxShadow: "none",
    minWidth: 0,
    padding: 0,
  },
  mediaBubble: {
    maxWidth: 330,
    minWidth: 220,
  },

  sticker: {
    width: 128,
    height: 128,
    objectFit: "contain",
    display: "block",
  },

  stickerFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 2,
    padding: "2px 4px",
  },

  InternalMessague: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.mode === "light" ? "#FFA08A" : "#5c4a26",
    color: theme.palette.mode === "light" ? "#303030" : "#f5e6c8",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.mode === "light" ? "0 1px 1px #b3b3b3" : "0 1px 1px rgba(0,0,0,0.4)",
  },

  AuditoriaMessague: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#9f2e2e",
    color: "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.mode === "light" ? "#cfe9ba" : "#04473c",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    fontSize: 14,
    lineHeight: "19px",
    userSelect: "text",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.36)" : "rgba(255, 255, 255, 0.5)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    fontSize: 14,
    lineHeight: "19px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.mode === "light" ? "#999" : "rgba(255,255,255,0.6)",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.mode === "light" ? "#e1f3fb" : "#182229",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: theme.palette.mode === "light" ? "0 1px 1px #b3b3b3" : "0 1px 1px rgba(0,0,0,0.4)",
  },

  dailyTimestampText: {
    color: theme.palette.mode === "light" ? "#808888" : "rgba(255,255,255,0.75)",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  canalDivider: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    margin: "14px 0 6px",
    userSelect: "none",
  },

  canalLinea: {
    flex: 1,
    height: 1,
    backgroundColor:
      theme.palette.mode === "light" ? "rgba(0,0,0,0.10)" : "rgba(255,255,255,0.14)",
  },

  notaIzquierda: {
    marginRight: 20,
    marginTop: 2,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    alignSelf: "flex-start",
    fontStyle: "italic",
    backgroundColor: theme.palette.mode === "light" ? "#fdf3e3" : "#3a3324",
    color: theme.palette.mode === "light" ? "#7a6023" : "#e0cfa4",
    border: `1px dashed ${theme.palette.mode === "light" ? "#e3cfa0" : "#5c4a26"}`,
    borderRadius: 8,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 5,
    paddingBottom: 0,
  },

  canalPildora: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    padding: "3px 10px",
    borderRadius: 20,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#182229",
    boxShadow:
      theme.palette.mode === "light"
        ? "0 1px 2px rgba(0,0,0,0.12)"
        : "0 1px 2px rgba(0,0,0,0.5)",
    whiteSpace: "nowrap",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackErrorIcon: {
    color: red[600],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
  documentCard: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    minWidth: 230,
    maxWidth: 330,
    padding: "10px 12px",
    margin: "2px 0 8px",
    borderRadius: 10,
    textDecoration: "none",
    color: "inherit",
    backgroundColor: theme.palette.mode === "light" ? "rgba(0,0,0,0.045)" : "rgba(255,255,255,0.10)",
    border: `1px solid ${theme.palette.mode === "light" ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.10)"}`,
    transition: "background-color .15s ease",
    "&:hover": {
      backgroundColor: theme.palette.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.16)",
    },
  },
  documentIcon: {
    flexShrink: 0,
    width: 38,
    height: 38,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.3,
    color: "#fff",
  },
  documentInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  documentName: {
    fontSize: 13.5,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  documentExt: {
    fontSize: 11,
    opacity: 0.7,
    textTransform: "uppercase",
  },
  documentDownload: {
    flexShrink: 0,
    fontSize: 20,
    color: "currentColor",
    opacity: 0.55,
  },
  imageLocation: {
    position: 'relative',
    width: '100%',
    height: 80,
    borderRadius: 5
  },
  metaNotice: {
    width: "100%",
    display: "flex",
    padding: "5px",
    alignItems: "center",
    backgroundColor: theme.palette.mode === "light" ? "#E1F3FB" : "#0d2430",
    color: theme.palette.text.primary,
  },

  scrollToBottomFab: {
    position: "absolute",
    right: 16,
    bottom: 16,
    zIndex: 2,
  },

  searchBar: {
    position: "absolute",
    top: 8,
    right: 8,
    left: 8,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    padding: "2px 8px",
    gap: 4,
  },

  messageHighlighted: {
    outline: `2px solid ${theme.palette.mode === "light" ? "#ffb300" : "#ffca28"}`,
    outlineOffset: 2,
  },

  reactionBadge: {
    position: "absolute",
    bottom: -8,
    right: 4,
    fontSize: 14,
    lineHeight: 1,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#233138",
    borderRadius: "50%",
    padding: 2,
    boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {

    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];




  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticket, ticketId, isGroup, onWindowStatusChange, searchOpen, starredOnly, onCloseSearch }) => {
  const { classes, cx } = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const didScrollBottomRef = useRef(false);
  const messageRefs = useRef({});

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const [traceMessage, setTraceMessage] = useState(null);
  const currentTicketId = useRef(ticketId);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const matches = useMemo(() => {
    if (starredOnly) {
      return messagesList.filter((m) => m.isStarred).map((m) => m.id);
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return messagesList
      .filter((m) => m.body?.toLowerCase().includes(term))
      .map((m) => m.id);
  }, [searchTerm, messagesList, starredOnly]);

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    if (matches.length === 0) return;
    const el = messageRefs.current[matches[currentMatchIndex]];
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [matches, currentMatchIndex]);

  const goToMatch = (step) => {
    if (matches.length === 0) return;
    setCurrentMatchIndex(
      (prev) => (prev + step + matches.length) % matches.length
    );
  };

  const handleCloseSearch = () => {
    setSearchTerm("");
    if (typeof onCloseSearch === "function") onCloseSearch();
  };

  useEffect(() => {
    if (!searchOpen) setSearchTerm("");
  }, [searchOpen]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
    didScrollBottomRef.current = false;
  }, [ticketId]);

  useEffect(() => {
    if (!didScrollBottomRef.current && pageNumber === 1 && messagesList.length > 1) {
      didScrollBottomRef.current = true;
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesList.length]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }

        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    if (!ticket.id) return undefined;

    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on("connect", () => socket.emit("joinChatBox", `${ticket.id}`));

    socket.on(`company-${companyId}-appMessage`, (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });


      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticket.id]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    const doScroll = () => {
      const el = document.getElementById("messagesList");
      if (el) el.scrollTop = el.scrollHeight;
    };
    setTimeout(doScroll, 0);
    setTimeout(doScroll, 300);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 200);

    if (!hasMore) return;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (isSticker(message)) {
      return (
        <img
          className={classes.sticker}
          src={message.mediaUrl}
          alt="sticker"
          loading="lazy"
        />
      );
    }
    if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    }
    if (message.mediaType === "audio") {
      return <AudioPlayer src={message.mediaUrl} fromMe={message.fromMe} />;
    }

    if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      let fileName = "Archivo";
      let ext = "";
      try {
        const fromUrl = decodeURIComponent(
          (message.mediaUrl || "").split("/").pop().split("?")[0]
        );
        fileName = fromUrl || "Archivo";
        const dot = fileName.lastIndexOf(".");
        if (dot > -1) ext = fileName.slice(dot + 1);
      } catch (e) {}

      const sinNombre = /^[0-9]+$/.test(fileName.replace(/.[^.]*$/, ""));

      const TIPOS = {
        pdf: { texto: "Documento PDF", color: "#d93025" },
        doc: { texto: "Documento Word", color: "#2b579a" },
        docx: { texto: "Documento Word", color: "#2b579a" },
        xls: { texto: "Hoja de cálculo", color: "#1d6f42" },
        xlsx: { texto: "Hoja de cálculo", color: "#1d6f42" },
        csv: { texto: "Hoja de cálculo", color: "#1d6f42" },
        ppt: { texto: "Presentación", color: "#d24726" },
        pptx: { texto: "Presentación", color: "#d24726" },
        zip: { texto: "Comprimido", color: "#8b6d3f" },
        rar: { texto: "Comprimido", color: "#8b6d3f" }
      };

      const tipo = TIPOS[String(ext).toLowerCase()];
      const titulo = sinNombre ? (tipo ? tipo.texto : "Archivo adjunto") : fileName;
      const colorTipo = tipo ? tipo.color : "#5a6b7b";

      return (
        <a
          className={classes.documentCard}
          href={message.mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
        >
          <span
            className={classes.documentIcon}
            style={{ backgroundColor: colorTipo }}
          >
            {ext ? String(ext).toUpperCase().slice(0, 4) : <InsertDriveFileOutlined style={{ fontSize: 20 }} />}
          </span>
          <div className={classes.documentInfo}>
            <span className={classes.documentName}>{titulo}</span>
            <span className={classes.documentExt}>
              {sinNombre ? "Pulsa para abrir" : (tipo ? tipo.texto : ext)}
            </span>
          </div>
          <GetApp className={classes.documentDownload} />
        </a>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === -1) {
      return (
        <Tooltip title="No entregado. WhatsApp no pudo entregar este mensaje al destinatario.">
          <ErrorOutlined fontSize="small" className={classes.ackErrorIcon} />
        </Tooltip>
      );
    }
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    const esUltimo = index === messagesList.length - 1;

    const cambiaDia =
      index === 0 ||
      !isSameDay(
        parseISO(messagesList[index].createdAt),
        parseISO(messagesList[index - 1].createdAt)
      );

    const fecha = cambiaDia ? (
      <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
        <div className={classes.dailyTimestampText}>
          {format(parseISO(messagesList[index].createdAt), "MM/dd/yyyy")}
        </div>
      </span>
    ) : null;

    if (!esUltimo) return fecha;

    return (
      <React.Fragment key={`ultimo-${message.id}`}>
        {fecha}
        <div ref={lastMessageRef} style={{ float: "left", clear: "both" }} />
      </React.Fragment>
    );
  };

  const CANALES = {
    whatsapp: { texto: "WhatsApp", color: "#25d366", Icono: WhatsApp },
    whatsapp_cloud: { texto: "WhatsApp Cloud", color: "#25d3c1", Icono: WhatsApp },
    webchat: { texto: "Chat web", color: "#3956ff", Icono: Language },
    facebook: { texto: "Messenger", color: "#3b5998", Icono: Facebook },
    instagram: { texto: "Instagram", color: "#e1306c", Icono: Instagram },
    sandbox: { texto: "Sandbox", color: "#8b8f9a", Icono: Science },
  };

  const esNota = (m) =>
    m?.mediaType === "internalMessague" || m?.mediaType === "auditoriaMessague";

  const canalPrevio = (index) => {
    for (let i = index - 1; i >= 0; i--) {
      if (!esNota(messagesList[i])) return messagesList[i]?.channel ?? null;
    }
    return null;
  };

  const renderCanalDivider = (message, index) => {
    if (esNota(message)) return null;

    const canal = message?.channel;
    if (!canal) return null;

    const anterior = canalPrevio(index);
    if (anterior !== null && canal === anterior) return null;

    const info = CANALES[canal];
    if (!info) return null;

    const { Icono } = info;

    return (
      <span className={classes.canalDivider} key={`canal-${message.id}`}>
        <span className={classes.canalLinea} />
        <span className={classes.canalPildora} style={{ color: info.color }}>
          <Icono style={{ fontSize: 15 }} />
          {info.texto}
        </span>
        <span className={classes.canalLinea} />
      </span>
    );
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={cx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={cx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const isVCard = (message) => {
    return message.includes('BEGIN:VCARD');
  };

  const isSticker = (message) =>
    message?.mediaType === "image" &&
    typeof message?.mediaUrl === "string" &&
    message.mediaUrl.toLowerCase().includes(".webp");

  const vCard = (message) => {
    const name = message?.substring(message.indexOf("N:;") + 3, message.indexOf(";;;"))
    const description = message?.substring(message.indexOf("TION:") + 5, message.indexOf("TEL"))
    return (
      <div>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 20, marginBottom: 20 }}>
          <Avatar style={{ marginRight: 10, marginLeft: 20, width: 60, height: 60 }} />
          <div style={{ width: 350 }}>
            <div>
              <Typography
                noWrap
                component="h4"
                variant="body2"
                color="textPrimary"
                style={{ fontWeight: '700' }}
              >
                {name}
              </Typography>
            </div>

            <div style={{ width: 350 }}>
              <Typography
                component="span"
                variant="body2"
                color="textPrimary"
                style={{ display: 'flex' }}
              >
                {description}
              </Typography>
            </div>
          </div>

        </div>
        <div style={{
          width: '100%', display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 20,
          borderWidth: '1px 0 0 0',
          borderTopColor: '#bdbdbd',
          borderStyle: 'solid',
          padding: 8
        }}>
          <Typography
            noWrap
            component="h4"
            variant="body2"
            color="textPrimary"
            style={{ fontWeight: '700', color: '#2c9ce7' }}
          >
            Conversar
          </Typography>
        </div>
      </div>
    )
  };

  const messageLocation = (message, createdAt) => {
    return (
      <div className={[classes.textContentItem, { display: 'flex', padding: 5 }]}>
        <img src={message.split('|')[0]} className={classes.imageLocation} />
        <a
          style={{
            display: 'block',
            marginTop: 4,
            fontWeight: 600,
            fontSize: 13,
            color: '#1976d2',
            textDecoration: 'none',
          }}
          target="_blank"
          rel="noopener noreferrer"
          href={message.split('|')[1]}> Ver ubicación en Google Maps</a>
        <span className={classes.timestamp}>
          {format(parseISO(createdAt), "HH:mm")}
        </span>
      </div>
    )
  };


  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderCanalDivider(message, index)}
              {renderMessageDivider(message, index)}
              <div
                ref={(el) => { messageRefs.current[message.id] = el; }}
                className={cx(
                  esNota(message) ? classes.notaIzquierda : classes.messageLeft,
                  {
                    [classes.messageHighlighted]: message.id === matches[currentMatchIndex],
                    [classes.stickerBubble]: isSticker(message),
                    [classes.mediaBubble]: message.mediaType === "image" && !isSticker(message),
                  })}
                title={message.queueId && message.queue?.name}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.reaction && (
                  <span className={classes.reactionBadge}>{message.reaction}</span>
                )}
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}

                {message.mediaUrl && checkMessageMedia(message)}

                {message.body.includes('data:image') ? messageLocation(message.body, message.createdAt)
                  :
                  isVCard(message.body) ?
                    <div className={[classes.textContentItem, { marginRight: 0 }]}>
                      {vCard(message.body)}
                    </div>

                    :


                    (<div className={classes.textContentItem}>
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {message.mediaType !== "audio" && !isSticker(message) && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                      <span className={classes.timestamp}>
                        {message.isStarred && <Star style={{ fontSize: 13, color: "#f5b400", verticalAlign: "middle", marginRight: 2 }} />}
                        {format(parseISO(message.createdAt), "HH:mm")}
                      </span>
                    </div>)}
              </div>
            </React.Fragment>
          );
        } else {

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderCanalDivider(message, index)}
              {renderMessageDivider(message, index)}
              <div
                ref={(el) => { messageRefs.current[message.id] = el; }}
                className={cx(message.mediaType === "internalMessague"
                  ? classes.InternalMessague
                  : message.mediaType === "auditoriaMessague"
                    ? classes.AuditoriaMessague
                    : classes.messageRight
                , {
                  [classes.messageHighlighted]: message.id === matches[currentMatchIndex],
                  [classes.stickerBubble]: isSticker(message),
                  [classes.mediaBubble]: message.mediaType === "image" && !isSticker(message),
                })}
                title={message.queueId && message.queue?.name}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.reaction && (
                  <span className={classes.reactionBadge}>{message.reaction}</span>
                )}
                {message.mediaUrl && checkMessageMedia(message)}
                <div
                  className={cx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.body.includes('data:image') ? messageLocation(message.body, message.createdAt)
                    :
                    isVCard(message.body) ?
                      <div className={[classes.textContentItem]}>
                        {vCard(message.body)}
                      </div>

                      :
                      message.quotedMsg && renderQuotedMessage(message)}
                  {message.mediaType !== "audio" && !isSticker(message) && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                  <span className={classes.timestamp}>
                    {message.isStarred && <Star style={{ fontSize: 13, color: "#f5b400", verticalAlign: "middle", marginRight: 2 }} />}
                    {message.isBot && message.toolTrace?.length > 0 && (
                      <Tooltip title="Ver qué herramienta usó la IA para esta respuesta">
                        <Science
                          style={{ fontSize: 13, verticalAlign: "middle", marginRight: 2, cursor: "pointer", opacity: 0.7 }}
                          onClick={() => setTraceMessage(message)}
                        />
                      </Tooltip>
                    )}
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>¡Saluda a tu nuevo contacto!</div>;
    }
  };

  const renderedMessages = useMemo(
    () => (messagesList.length > 0 ? renderMessages() : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messagesList, matches, currentMatchIndex]
  );



  const isCloudChannel = ticket?.channel === "whatsapp_cloud";
  const lastCustomerMessage = [...messagesList]
    .reverse()
    .find((m) => {
      if (m.fromMe) return false;
      if (!isCloudChannel) return true;
      if (m.channel !== "whatsapp_cloud") return false;
      if (m.whatsappId == null || ticket?.whatsappId == null) return true;
      return String(m.whatsappId) === String(ticket.whatsappId);
    });
  const replyLimit = lastCustomerMessage ? addHours(parseISO(lastCustomerMessage.createdAt), 24) : null;
  const now = new Date();
  const adjustedReplyLimit = replyLimit ? new Date(replyLimit.getTime() - 10 * 60 * 1000) : null;
  const canStillReply = adjustedReplyLimit && now <= adjustedReplyLimit;

  useEffect(() => {
    if (typeof onWindowStatusChange === "function") {
      onWindowStatusChange({
        hasCustomerMessage: !!lastCustomerMessage,
        canStillReply: !!canStillReply,
        replyLimit,
        loaded: !loading,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, lastCustomerMessage?.id, canStillReply, loading]);

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        ticket={ticket}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <Dialog open={Boolean(traceMessage)} onClose={() => setTraceMessage(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Qué usó la IA para esta respuesta</DialogTitle>
        <DialogContent dividers>
          <ToolTraceView toolTrace={traceMessage?.toolTrace} />
        </DialogContent>
      </Dialog>
      {searchOpen ? (
        <Paper className={classes.searchBar} elevation={3}>
          {starredOnly ? (
            <>
              <Star fontSize="small" style={{ color: "#f5b400" }} />
              <Typography variant="body2" noWrap style={{ flex: 1 }}>
                Destacados
              </Typography>
            </>
          ) : (
            <>
              <Search fontSize="small" />
              <TextField
                autoFocus
                variant="standard"
                placeholder="Buscar en la conversación"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") goToMatch(e.shiftKey ? -1 : 1);
                  if (e.key === "Escape") handleCloseSearch();
                }}
                slotProps={{ input: { disableUnderline: true } }}
              />
            </>
          )}
          {(starredOnly || searchTerm.trim()) && (
            <Typography variant="caption" color="textSecondary" noWrap>
              {matches.length > 0 ? `${currentMatchIndex + 1}/${matches.length}` : "0/0"}
            </Typography>
          )}
          <IconButton size="small" onClick={() => goToMatch(-1)} disabled={matches.length === 0}>
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => goToMatch(1)} disabled={matches.length === 0}>
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCloseSearch}>
            <Close fontSize="small" />
          </IconButton>
        </Paper>
      ) : null}
      {(ticket?.adReferralCampaign || ticket?.adReferralTitle) && (
        <div className={classes.adReferralBanner}>
          <Campaign fontSize="small" />
          <span>
            Este contacto escribió desde{" "}
            {ticket.adReferralSource ? `${ticket.adReferralSource}, ` : ""}
            campaña: <b>{ticket.adReferralCampaign || ticket.adReferralTitle}</b>
          </span>
        </div>
      )}
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {renderedMessages}
      </div>
      {showScrollButton && (
        <Fab
          size="small"
          className={classes.scrollToBottomFab}
          onClick={scrollToBottom}
        >
          <ArrowDownward />
        </Fab>
      )}
      {["instagram", "facebook", "whatsapp_cloud"].includes(ticket?.channel) && (

        <div className={classes.metaNotice}>
          {ticket?.channel === "facebook" ? (
            <Facebook small />
          ) : (
            <Instagram small />
          )}


          {replyLimit ? (
            <span>
              {canStillReply
                ? ` Puedes responderle hasta ${format(replyLimit, "dd/MM HH:mm")}.`
                : ` Pasó el tiempo para responderle por chat. Envíale una plantilla y espera a que responda.`}
            </span>
          ) : isCloudChannel ? (
            <span>
              {` El cliente aún no ha escrito. Envíale una plantilla para iniciar y espera su respuesta.`}
            </span>
          ) : null}
        </div>
      )}
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;